/**
 * Simple Minmaxify Integration
 * 
 * When Mask Type variant changes, refresh the quantity box to show new minimum quantity
 */

import { ThemeEvents } from '@theme/events';

// Check if this product has Mask Type variants
function hasMaskTypeVariant() {
  const legends = document.querySelectorAll('fieldset legend');
  for (const legend of legends) {
    if (legend.textContent && legend.textContent.toLowerCase().includes('mask type')) {
      return true;
    }
  }
  return false;
}

// Check if the variant change is for Mask Type
function isMaskTypeChange(event) {
  // Method 1: Check document.activeElement (works on desktop)
  const clickedElement = document.activeElement;
  if (clickedElement) {
    const parentFieldset = clickedElement.closest('fieldset');
    if (parentFieldset) {
      const legend = parentFieldset.querySelector('legend');
      const legendText = legend?.textContent?.toLowerCase() || '';
      if (legendText.includes('mask type')) {
        return true;
      }
    }
  }
  
  // Method 2: Check event.target (works on mobile)
  if (event.target) {
    const parentFieldset = event.target.closest('fieldset');
    if (parentFieldset) {
      const legend = parentFieldset.querySelector('legend');
      const legendText = legend?.textContent?.toLowerCase() || '';
      if (legendText.includes('mask type')) {
        return true;
      }
    }
  }
  
  // Method 3: Check all Mask Type fieldsets for recent activity
  const maskTypeFieldsets = Array.from(document.querySelectorAll('fieldset')).filter(fieldset => {
    const legend = fieldset.querySelector('legend');
    return legend && legend.textContent.toLowerCase().includes('mask type');
  });
  
  for (const fieldset of maskTypeFieldsets) {
    const inputs = fieldset.querySelectorAll('input, select');
    for (const input of inputs) {
      if (input === document.activeElement || input === event.target) {
        return true;
      }
    }
  }
  
  return false;
}

// Refresh quantity box
function refreshQuantityBox(variantId) {
  const quantitySelector = document.querySelector('quantity-selector-component');
  if (!quantitySelector) return;

  const url = new URL(window.location.href);
  url.searchParams.set('variant', variantId);
  
  fetch(url.toString())
    .then(response => response.text())
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newQuantitySelector = doc.querySelector('quantity-selector-component');
      
      if (newQuantitySelector) {
        // Hide current selector to prevent flicker
        quantitySelector.style.opacity = '0';
        
        // Pre-set correct minimum value
        const newInput = newQuantitySelector.querySelector('input[name="quantity"]');
        const correctMin = newInput?.getAttribute('mm-min') || newInput?.getAttribute('min') || '1';
        if (newInput) {
          newInput.value = correctMin;
        }
        
        // Replace the quantity selector
        quantitySelector.outerHTML = newQuantitySelector.outerHTML;
        
        // Reinitialize Minmaxify
        if (window.Minmaxify && typeof window.Minmaxify.refresh === 'function') {
          window.Minmaxify.refresh();
        }
        if (window.Minmaxify && typeof window.Minmaxify.init === 'function') {
          window.Minmaxify.init();
        }
        
        // Trigger events for Minmaxify
        const newQuantityElement = document.querySelector('quantity-selector-component');
        if (newQuantityElement) {
          newQuantityElement.style.opacity = '1';
          newQuantityElement.dispatchEvent(new Event('minmaxify:refresh', { bubbles: true }));
        }
        
        const quantityInput = document.querySelector('quantity-selector-component input[name="quantity"]');
        if (quantityInput) {
          quantityInput.dispatchEvent(new Event('DOMNodeInserted', { bubbles: true }));
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    })
    .catch(() => {}); // Silent error handling
}

// Set up the integration
function setupIntegration() {
  if (!hasMaskTypeVariant()) return;
  
  document.addEventListener(ThemeEvents.variantUpdate, function(event) {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Mobile: refresh on any variant change for Mask Type products
    if (isMobile) {
      if (hasMaskTypeVariant()) {
        let variantId = event.detail.resource?.id || event.detail.variant?.id;
        if (variantId) {
          refreshQuantityBox(variantId);
        }
        return;
      }
    }
    
    // Desktop: only refresh on Mask Type changes
    if (!isMaskTypeChange(event)) return;
    
    let variantId = event.detail.resource?.id || event.detail.variant?.id;
    if (variantId) {
      refreshQuantityBox(variantId);
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupIntegration);
} else {
  setupIntegration();
}